import { Injectable, OnInit } from '@angular/core';
import html2canvas from 'html2canvas';
import jspdf from 'jspdf';
import { RadiologyEhrData } from '../@core/interfaces/ehr/radiology-ehr';
import { DateService } from '../@theme/components/common/service/date.service';
import { environment } from '../../environments/environment';
import { PrinterSettings, PrinterSettingsData } from '../@core/interfaces/masters/printersettings';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs';
import { PrintServiceService } from '../@theme/components/common/service/print-service.service';
import { DatePipe } from '@angular/common';
import { LabResultData } from '../@core/interfaces/lab/lab-result-detail';
import { RolesData } from '../@core/interfaces/common/roles';
import { imageatch_detData } from '../@core/interfaces/masters/imageatch_det';
import { TenantData } from '../@core/interfaces/masters/tenant';
import { WhatsappIntegration } from '../@core/interfaces/common/whatsappIntegration';
import { ToastrAppSrvcService } from '../_services/toastr-app-srvc.service';
import { MailIntegration } from '../@core/interfaces/common/mailIntegration';

@Injectable({
  providedIn: 'root'
})
export class PdfToBlobService{
  pdfcontent: Blob;
  private tenantId = localStorage.getItem("tenantId");
  private branchId = localStorage.getItem("branchId");
  psDetails:PrinterSettings;
  protected readonly unsubscribe$ = new Subject<void>();
  get apiUrl(): string { return environment.apiUrl; }

  // ABHA "Save Report" HI Type → attachment Category/SubCategory/FileName,
  // single source of truth so the three values can't drift out of sync.
  // Applied inside downloadPDFsaveReportABHA() only when comp?.isAbhaSave is
  // set (by CareContextLinkingComponent), because resourceType values like
  // "Immunization" and "Prescription" are also passed to this same method by
  // unrelated non-ABHA callers (vaccine schedule, prescription print).
  private static readonly ABHA_HI_TYPE_BLOB_CONFIG: Record<string, { category: string; subCategory: string; fileName: string }> = {
    DiagnosticReport: { category: 'Lab', subCategory: 'abha', fileName: 'DiagnosticReport' },
    Prescription: { category: 'Pharmacy', subCategory: 'abha', fileName: 'Prescription' },
    Immunization: { category: 'Immunization', subCategory: 'abha', fileName: 'ImmunizationRecord' },
    WellnessRecord: { category: 'Wellness', subCategory: 'abha', fileName: 'WellnessRecord' },
    OPConsultation: { category: 'ClinicalRecord', subCategory: 'abha', fileName: 'OPConsultation' },
    DischargeSummary: { category: 'DischargeSummary', subCategory: 'abha', fileName: 'DischargeSummary' },
  };

  constructor(protected MailService : MailIntegration,public toaster: ToastrAppSrvcService,protected whatsappService : WhatsappIntegration ,protected tenantSrvc: TenantData,private iatchSrvc: imageatch_detData,protected rolesService: RolesData,private radiologySrvc: RadiologyEhrData,private datePipe: DatePipe,public datService: DateService,public PrintServiceService: PrintServiceService,private psSrvc:PrinterSettingsData)  {
   }
 
  // Loads the tenant printer settings once per session and caches them on this
  // root-provided service: every downloadPDF*/saveReport call used to re-fetch
  // the same "Lab Result Entry" layout (one request per generated PDF, i.e.
  // per HI Type per row for the ABHA Save Report flow). Returns a promise that
  // actually resolves once psDetails is populated, so callers awaiting it can
  // rely on psDetails being set.
  async loadPSF(): Promise<void>
  {
    if (this.psDetails) { return; }
    return new Promise<void>(resolve => {
      this.psSrvc.list(this.tenantId +"/"+this.branchId+ "/printersettings").pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
        var ps=data?.filter(ps=>ps.pageSetupFor=="Lab Result Entry") ?? [];
        if(ps.length>0){
          this.psDetails=this.PrintServiceService.onSetPsData(ps[0]);
        }else{
          this.psDetails=this.PrintServiceService.onGetPrinterSettings();
        }
      }, (error) => {
        this.psDetails=this.PrintServiceService.onGetPrinterSettings();
        resolve();
      }, () => {
        resolve();
      });
    });
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async downloadPDFForHims(data1,patientName,regId,comp,psDetails,fileName,category,page,type:'Mail'|'Whatsapp'|'Qr-Code',event?,option?,tempPageName?,from?) {
    if(!!event && type==='Mail' || type==='Whatsapp'){
      event.target.disabled=true;
    }
    if(type==='Mail' || type==='Whatsapp'){
      comp.loadPer = 30;
      comp.showPer = true;
    }
    
    try{
      html2canvas(data1).then(canvas => {
        // Calculate suitable page size
        psDetails.orientation = "Portrait";
        psDetails.paperSize = "A4";
        // `true` enables jsPDF's own lossless FlateEncode compression for the
        // embedded page images (addImage below doesn't pass an explicit
        // compression level, so jsPDF applies its own "SLOW"/maximum level
        // once this flag is on) — shrinks large multi-page PDFs enough to
        // avoid 413 Request Entity Too Large on callAtchForHims()'s upload,
        // without touching capture resolution/format/quality.
        let pdf = new jspdf(psDetails.orientation === "Portrait" ? 'p' : 'l', 'cm', (psDetails.paperSize).toLowerCase(), true);
  
        var width = pdf.internal.pageSize.getWidth();
        var height = pdf.internal.pageSize.getHeight();
  
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = width; // Use the full page width
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
        let position = 0;
        const pageHeight = pdf.internal.pageSize.getHeight();
  
        while (position < imgHeight) {
            // Add a new page if content doesn't fit
            if (position !== 0) {
                pdf.addPage();
            }
  
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, Math.min(pageHeight, imgHeight - position));
            position += pageHeight;
        }
        var pageCount = pdf.internal.pages.length;
        if (pageCount > 2) {
          var pageToDeleteIndex = pageCount - 1; // Index of the last page
          pdf.deletePage(pageToDeleteIndex);
        }
        var filename = !!patientName ? patientName.replace(/\s/g, '') + fileName + ".pdf" : '';
        this.pdfcontent = pdf.output('blob');
        if(type==='Mail' || type==='Whatsapp'){
          return this.callAtchForHims(this.pdfcontent, filename,comp,type,category,page,regId,event,option,tempPageName,from);
        }
        else if(type==='Qr-Code'){
          return this.callAtchForHims(this.pdfcontent, filename,comp,type,category,page,regId,option,tempPageName,from);
        }
        
    });
    }
    catch(e){
      if(type==='Qr-Code'){
        comp.spinner.hide()
      }
    }
    
  }
  
   
  callAtchForHims(data,filename,comp,type,category,page,regId,event?,option?,tempPageName?,from?): void{
    this.msgPageName = tempPageName
    if(type==='Mail' || type==='Whatsapp'){
        comp.loadPer = 80;
    }
    const file = new File([data], filename, { type: 'application/pdf' });
    const formData: FormData = new FormData();
    formData.append('fileKey', file,file.name);
  
    let postData= {
      regId: regId,
      tenantId: this.tenantId,
      branchId: this.branchId,
      containerName: this.tenantId,
      fileLocation: `${this.tenantId}/${this.branchId}/${regId}/${page}/${file.name}`,
      fileName: file.name,
      category:category,//
      doneDate: this.datService.currentDate(),
      doneBy:localStorage.getItem('userName'),
      fileAddedBy: "system",
      notes: null,//
      tagType: "none",
      fileAddedFrom: page,//
      isEdit:true,
      isCreate:true,
      WhatsappSentStatus:"Y",
      WhatsappSentDateTime:new Date()
    }
    
    for (const prop in postData) {
      if (!postData.hasOwnProperty(prop)) { continue; }
      formData.append(prop , postData[prop]);
    }

    if(type==='Qr-Code'){
      postData.WhatsappSentStatus='N'
    }
   
    this.radiologySrvc.create(`${this.apiUrl}/AtchDet/${this.tenantId}/${this.branchId}/addImageDetail`,formData).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
      if(type==='Mail' || type==='Whatsapp'){
      comp.loadPer = 100;   
      comp.showPer = false; 
      // comp.sendMail(data);
      if(type==='Whatsapp' || type==='Mail'){
        this.checkMsgPossibility(option,comp , data?.uri , data,filename,from,category)
      }
      }
      else if(type==='Qr-Code'){
        comp.blobUrl(data?.uri)
      }
    
    }, (error) => {
      if(type==='Mail' || type==='Whatsapp' && !!event){
      comp.showPer = false; 
      event.target.disabled=false;
      }
      else if(type==='Qr-Code'){
        comp.spinner.hide()
      }
      
    }, () => {
      if(type==='Mail' || type==='Whatsapp' && !!event){
        event.target.disabled=false;
      }
      else if(type==='Qr-Code'){
        comp.spinner.hide()
      }
    });
  }

msgPageName : string = ''
whatsAppToken:any
whatsAppUrl:any
pageName:any
tenantData:any
whatsappcontent: string;
emailcontent: string;
smscontent: string;
checkMsgPossibility(option , patientData,url,fullData,fileName,from,category,msgPageName?){
    let istemplatepurchase;
    let paticentDetails;
    let headerUrl = "";
    if(msgPageName){
      this.msgPageName = msgPageName
    }
    this.tenantSrvc.getById(`${this.apiUrl}/tenants/${this.tenantId}/tenantmst/${this.tenantId}`).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
      this.tenantData = data;
    this.rolesService.list(`PatientRegistrationMst/${this.tenantId}/${this.branchId}/getPatientBasicDetailByRegId?regId=${ category === 'OP' ? patientData?.patientDetails?.id : category === 'Lab' ? patientData?.patientDetails?.regMst?.id : category === 'IP' ? patientData?.patientDetails?.id : category === 'Appointment_Slip' ? patientData?.appointmentAllList[0]?.patientRegistration?.id : category === 'OPD_Slip' ? patientData?.appointmentData?.patientRegistration?.id : category === 'prescription' ? patientData?.regDetails?.id : '' }`).subscribe((Details:any) =>{
      paticentDetails = Details?.details 

      this.rolesService.list(`tenantSmsTemplatePurchaseMst/${this.tenantId}/${this.branchId}/GetCommDetByTemplateName?templateName=${  this.msgPageName  +'-'+ option}`).subscribe((data:any) => {
      if(!!data?.details){
        istemplatepurchase = data?.details
        if(from === 'sms' ){
                if(this.tenantData.smsActivationFlag){

        if (istemplatepurchase?.smsFlag) {
            if(istemplatepurchase?.creditBalance > 0 ){
              if(istemplatepurchase?.smsContent){
                let message = istemplatepurchase?.smsContent || "";
                this.smscontent =  
                category === 'OP' ?
                message?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.firstname)
                  .replace((new RegExp("{billnumber}", "g")),patientData?.billData?.billMaster?.billNumber || 0)
                  .replace((new RegExp("{billdate}", "g")),this.datService.dateUtctoLocal(patientData?.billData?.billMaster?.billDate,"DD-MMM-YYYY"))
                  .replace((new RegExp("{netamount}", "g")),patientData?.billData?.billMaster?.netAmount || 0)
                  .replace((new RegExp("{hospitalname}", "g")),localStorage.getItem('hospitalName'))
                  .replace((new RegExp("{regno}", "g")), patientData?.patientDetails?.regNum || 0)
                  .replace((new RegExp("{balanceamount}", "g")),patientData?.billData?.billMaster?.balanceAmount || 0)         
                  .replace((new RegExp("{hospitalname}", "g")), localStorage.getItem('hospitalName'))      
   
                  .replace((new RegExp("{date}", "g")), this.datService.dateUtctoLocal(new Date(),"DD-MMM-YYYY")) 
                  .replace((new RegExp("{file}", "g")), url ) 
                  .replace((new RegExp("{time}", "g")), this.datService.dateUtctoLocal(new Date(),"h:mm a"))                   
                  
                  .replace((new RegExp("{hospitalbranch}", "g")), patientData?.branchData?.branchName) 
                  .replace((new RegExp("{hospitalemailid}", "g")), patientData?.branchData?.email) 
                  .replace((new RegExp("{hospitalemail}", "g")), patientData?.branchData?.email) 
                  .replace((new RegExp("{currency}", "g")), patientData?.branchData?.currencySymbol)    
                  .replace((new RegExp("{hospitalphonenumber}", "g")),localStorage.getItem('HospitalMobile'))
                  :
                  category === 'Lab' ? 
                  message?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.regMst?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  :
                  category === 'IP' ? 
                  message?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  : 
                  category === 'Appointment_Slip' ? 
                  message?.replace((new RegExp("{patientname}", "g")), patientData?.appointmentAllList[0]?.patientRegistration?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  :
                  category === 'OPD_Slip' ? 
                  message?.replace((new RegExp("{patientname}", "g")), patientData?.appointmentData?.patientRegistration?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  :
                  category === 'prescription' ? 
                  message?.replace((new RegExp("{patientname}", "g")), patientData?.regDetails?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  : ''                               


              }
              
              this.sendSMS(patientData, istemplatepurchase, option ,category);
            }else{
              this.toaster.ForDanger('SMS Credit Balance is 0');
            }
        }

      }    
        }else if (from === 'whatsapp'){
      if(istemplatepurchase?.whatsappTemplateAtchId){
        this.iatchSrvc.list(`ImageAtchDet/${this.tenantId}/${this.branchId}/uri?id=${istemplatepurchase?.whatsappTemplateAtchId}`).subscribe((link:any) =>{
          headerUrl = link
          this.whatsappFn(istemplatepurchase , patientData ,   this.msgPageName , option ,headerUrl,fileName,category)

        })
      }else{
          headerUrl = url
          this.whatsappFn(istemplatepurchase , patientData ,  this.msgPageName , option ,headerUrl,fileName,category)      

      }
        }else if(from === 'email'){
      if(this.tenantData.mailActivationFlag){

        if(istemplatepurchase.emailFlag){

            if(istemplatepurchase.emailContent){            
              this.emailcontent=
              category === 'OP' ?
              istemplatepurchase.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.firstname)
                  .replace((new RegExp("{billnumber}", "g")),patientData?.billData?.billMaster?.billNumber || 0)
                  .replace((new RegExp("{billdate}", "g")),this.datService.dateUtctoLocal(patientData?.billData?.billMaster?.billDate,"DD-MMM-YYYY"))
                  .replace((new RegExp("{netamount}", "g")),patientData?.billData?.billMaster?.netAmount || 0)
                  .replace((new RegExp("{hospitalname}", "g")),localStorage.getItem('hospitalName'))
                  .replace((new RegExp("{regno}", "g")), patientData?.patientDetails?.regNum || 0)
                  .replace((new RegExp("{balanceamount}", "g")),patientData?.billData?.billMaster?.balanceAmount || 0)         
                  .replace((new RegExp("{hospitalname}", "g")), localStorage.getItem('hospitalName'))      
   
                  .replace((new RegExp("{date}", "g")), this.datService.dateUtctoLocal(new Date(),"DD-MMM-YYYY")) 
                  .replace((new RegExp("{pdfLink}", "g")), url ) 
                  .replace((new RegExp("{time}", "g")), this.datService.dateUtctoLocal(new Date(),"h:mm a"))                   
                  
                  .replace((new RegExp("{hospitalbranch}", "g")), patientData?.branchData?.branchName) 
                  .replace((new RegExp("{hospitalemailid}", "g")), patientData?.branchData?.email) 
                  .replace((new RegExp("{hospitalemail}", "g")), patientData?.branchData?.email) 
                  .replace((new RegExp("{currency}", "g")), patientData?.branchData?.currencySymbol)    
                  .replace((new RegExp("{hospitalphonenumber}", "g")),localStorage.getItem('HospitalMobile'))
                  :
              category === 'Lab' ? 
              istemplatepurchase?.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.regMst?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  :
              category === 'IP' ? 
              istemplatepurchase?.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.patientDetails?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), url || '')
                  :
              category === 'Appointment_Slip' ? 
              istemplatepurchase?.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.appointmentAllList[0]?.patientRegistration?.firstname || '')
                      .replace((new RegExp("{pdfLink}", "g")), url || '')
              :
              category === 'OPD_Slip' ? 
              istemplatepurchase?.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.appointmentData?.patientRegistration?.firstname || '')
                      .replace((new RegExp("{pdfLink}", "g")), url || '')
              :
              category === 'prescription' ? 
              istemplatepurchase?.emailContent?.replace((new RegExp("{patientname}", "g")), patientData?.regDetails?.firstname || '')
                      .replace((new RegExp("{pdfLink}", "g")), url || '')
              : ''                                  


            }
              if(istemplatepurchase.emailCreditBalance > 0){
                const loadData = {
                  "Content" : this.emailcontent , 
                  "isTemplate" : istemplatepurchase , 
                  "Branch" : category === 'OP' ? patientData?.branchData : category === 'Lab' ? patientData?.branchData : category === 'IP' ? patientData?.branchData : category === 'Appointment_Slip' ? patientData?.branchData : category === 'OPD_Slip' ? patientData?.branchData : category === 'prescription' ? patientData?.patDet?.branch?.branchName : '',
                  "Patient" : category === 'OP' ? patientData?.patientDetails : category === 'Lab' ? patientData?.patientDetails?.regMst : category === 'IP' ? patientData?.patientDetails : category === 'Appointment_Slip' ? patientData?.appointmentAllList[0]?.patientRegistration : category === 'OPD_Slip' ? patientData?.appointmentData?.patientRegistration : category === 'prescription' ? patientData?.regDetails : '', 
                  "Provider" : istemplatepurchase?.emailProviderName ,
                  "Subject" : `${ this.msgPageName } Email From ${category === 'OP' ? patientData?.branchData?.branchName : category === 'Lab' ? patientData?.patDet?.branch?.branchName : category === 'IP' ? patientData?.branchData?.branchName : category === 'Appointment_Slip' ? patientData?.branchData?.branchName : category === 'OPD_Slip' ? patientData?.branchData?.branchName : category === 'prescription' ? patientData?.regDetails?.branch?.branchName : '' }`,
                  "HtmlContact" : `<html><head><title></title></head><body><p>${this.emailcontent}</p></body></html>`
                   
                }

                
              if(category === 'OP' ? patientData?.patientDetails?.email : category === 'Lab' ? patientData?.patientDetails?.regMst?.email : category === 'IP' ? patientData?.patientDetails?.email : category === 'Appointment_Slip' ? patientData?.appointmentAllList[0]?.patientRegistration?.email : category === 'OPD_Slip' ? patientData?.appointmentData?.patientRegistration?.email : category === 'prescription' ? patientData?.regDetails?.email : ''){                          
               this.MailService.sendMailMessage(loadData).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
                if(data.response_type === "success"){

                        const whatsAppPostData = 
                        {
                          "referenceFrom": this.msgPageName+'-'+option,
                          "referenceNum": category === 'OP' ? patientData?.patientDetails?.regNum : category === 'Lab' ? patientData?.patientDetails?.regMst?.regNum : category === 'IP' ? patientData?.patientDetails?.regNum : category === 'Appointment_Slip' ? patientData?.appointmentAllList[0]?.patientRegistration?.regNum : category === 'OPD_Slip' ? patientData?.appointmentData?.patientRegistration?.regNum : category === 'prescription' ? patientData?.regDetails?.regNum : '',
                          "createdDateTime": new Date(),
                          "createdBy": parseInt(localStorage.getItem("userId")),
                          "receiverEmailId": category === 'OP' ? patientData?.patientDetails?.email : category === 'Lab' ? patientData?.patientDetails?.regMst?.email : category === 'IP' ? patientData?.patientDetails?.email : category === 'Appointment_Slip' ? patientData?.appointmentAllList[0]?.patientRegistration?.email : category === 'OPD_Slip' ? patientData?.appointmentData?.patientRegistration?.email : category === 'prescription' ? patientData?.regDetails?.email : '' ,
                          "emailMessage": this.emailcontent,
                          "emailUnit": 1,
                          "senderEmailId": istemplatepurchase.emailSenderMail,
                          "sentStatus": "Pending",
                          "tenantId": this.tenantId,
                          "branchId": this.branchId,
                          "emailConfigId": istemplatepurchase.emailConfigId
                        }

                        this.rolesService.create(`${this.apiUrl}/emailConfigMst/PostEmailTrackingDet`,whatsAppPostData).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
                    this.toaster.ForSuccess('Email Message Send sucessfull');                          
                        })                    
                }else{
                  this.toaster.ForDanger('Email Message Send Unsucessfull');
                }
               })
              }else{
                this.toaster.ForDanger('Email is Missing');
              }

              }else{
                this.toaster.ForDanger('Email Credit Balance is 0');
              }
        } else{
                patientData.loadPer = 100;   
                patientData.showPer = false; 
                patientData.sendMail(fullData);  
        }

      }else{

      patientData.loadPer = 100;   
      patientData.showPer = false; 
      patientData.sendMail(fullData);  

      }
        }
    






      }
    },(error)=>{
      if(this.tenantData.mailActivationFlag){
      patientData.loadPer = 100;   
      patientData.showPer = false; 
      patientData.sendMail(fullData);
      }else if (this.tenantData?.whatsAppActivationFlag){
      patientData.loadPer = 100;   
      patientData.showPer = false; 
      patientData.sendMail(fullData);        
      }

    }
  
  )
    })
    })    

}

  sendSMS(billMasterData, templateData, options ,category) {
    let smstrackingDetails = {
      referencefrom: this.msgPageName + '-' + options,

      referencenum: category === 'OP' ? billMasterData?.billData?.billMaster?.billNumber : category === 'Lab' ? billMasterData?.labworkorderId || 0 : category === 'IP' ? (billMasterData?.admissionId || billMasterData?.admissionNumber) || 0 : category === 'Appointment_Slip' ? (billMasterData?.appointmentAllList[0]?.appointmentId) || 0 : category === 'OPD_Slip' ? (billMasterData?.appointmentData?.appointmentId) || 0 : category === 'prescription' ? (billMasterData?.pagevisitId) || 0 : '',
      mobileNumber: category === 'OP' ? billMasterData?.patientDetails?.mobile : category === 'Lab' ? billMasterData?.patientDetails?.regMst?.mobile || 0 : category === 'IP' ? billMasterData?.patientDetails?.mobile || 0 : category === 'Appointment_Slip' ? (billMasterData?.appointmentAllList[0]?.patientRegistration?.mobile) || 0 : category === 'OPD_Slip' ? (billMasterData?.appointmentData?.appointmentId) || 0 : category === 'prescription' ? (billMasterData?.regDetails?.mobile) || 0 : '' ,
      dateTime: this.datService.currentDate(),
      message: this.smscontent,
      smsUnit: 1,
      status: "ok",

      senderId: templateData.smsServiceName,
      templateId: templateData.smsTemplateDetId,
      
      createdIp: null,
      createdby: parseInt(localStorage.getItem("userId")),
      createddate: this.datService.currentDate(),
      tenantId: this.tenantId,
      branchId: this.branchId
    }

    let smsContent = {
      text: this.smscontent,
      mobile: category === 'OP' ? billMasterData?.patientDetails?.mobile : category === 'Lab' ? billMasterData?.patientDetails?.regMst?.mobile || 0 : category === 'IP' ? billMasterData?.patientDetails?.mobile || 0 : category === 'Appointment_Slip' ? (billMasterData?.appointmentAllList[0]?.patientRegistration?.mobile) || 0 : category === 'OPD_Slip' ? (billMasterData?.appointmentData?.patientRegistration?.mobile) || 0 : category === 'prescription' ? (billMasterData?.regDetails?.mobile) || 0 : '' ,

      tenantApiConfigId:templateData?.smsConfigId,
      commonSmsTempId:templateData.commonTempMstId, 

      tenantId: this.tenantId,
      branchId: this.branchId,
      smstrackingDetails: smstrackingDetails
    }
    this.rolesService.create(`${this.apiUrl}/sms/send`, smsContent).subscribe(data => {
        if(data.code === 200){
          this.toaster.ForSuccess('PDF SMS Send Sucessfully');
        }else{
          this.toaster.ForDanger('PDF SMS Send Unsucessfull');
        }
    }, (error) => {
      this.toaster.ForDanger('PDF SMS Send Unsucessfull');
    }, () => {
    });
  }

whatsappFn(istemplatepurchase:any , billMaster:any , pageName:any , option:any ,Url?:any,filename?:any,category?:any){
    let paticentDetails;

    let showTextOrImage;
    let metaApiDetails;
    let headerUrl = Url;
    let bodyMessage = "";
    let bodyReplaceMessage = "";
    let bodyArray
      if(this.tenantData?.whatsAppActivationFlag){
      if (istemplatepurchase.whatsappFlag) {
        this.whatsappService.metaWhatsappMessageList(istemplatepurchase?.whatsappTemplateId , istemplatepurchase?.businessAppTokenNo).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
          metaApiDetails = data
          metaApiDetails?.components.forEach(x => {
            if(x.type === "HEADER" && x.format === "IMAGE"){
              showTextOrImage = "IMAGE"
            }
            if(x.type === "HEADER" && x.format === "DOCUMENT"){
              showTextOrImage = "DOCUMENT"
            }

            if(x.type === "HEADER" && x.format === "TEXT"){
              showTextOrImage = "TEXT"
            }
            if(x.type === "BODY"){
              bodyMessage = x?.text;
              bodyArray = x?.example?.body_text[0]
            }
          });
          if(bodyMessage){
              const matches = bodyArray.join(" ").match(/{.*?}/g)?.join(" , ") || "";

              const WithoutReplace =
              category === 'OP' ?
              matches?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.firstname)
                  .replace((new RegExp("{billnumber}", "g")),billMaster?.billData?.billMaster?.billNumber || 0)
                  .replace((new RegExp("{billdate}", "g")),this.datService.dateUtctoLocal(billMaster?.billData?.billMaster?.billDate,"DD-MMM-YYYY"))
                  .replace((new RegExp("{netamount}", "g")),billMaster?.billData?.billMaster?.netAmount || 0)
                  .replace((new RegExp("{hospitalname}", "g")),localStorage.getItem('hospitalName'))
                  .replace((new RegExp("{regno}", "g")), billMaster?.patientDetails?.regNum || 0)
                  .replace((new RegExp("{balanceamount}", "g")),billMaster?.billData?.billMaster?.balanceAmount || 0)         
                  .replace((new RegExp("{hospitalname}", "g")), localStorage.getItem('hospitalName'))      
   
                  .replace((new RegExp("{date}", "g")), this.datService.dateUtctoLocal(new Date(),"DD-MMM-YYYY")) 
                  .replace((new RegExp("{time}", "g")), this.datService.dateUtctoLocal(new Date(),"h:mm a"))                   
                  
                  .replace((new RegExp("{hospitalbranch}", "g")), billMaster?.branchData?.branchName) 
                  .replace((new RegExp("{hospitalemailid}", "g")), billMaster?.branchData?.email) 
                  .replace((new RegExp("{hospitalemail}", "g")), billMaster?.branchData?.email) 
                  .replace((new RegExp("{currency}", "g")), billMaster?.branchData?.currencySymbol)    
                  .replace((new RegExp("{hospitalphonenumber}", "g")),localStorage.getItem('HospitalMobile'))
                  :
              category === 'Lab' ? 
                  matches?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.regMst?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'IP' ?
                  matches?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  : 
              category === 'Appointment_Slip' ?
                  matches?.replace((new RegExp("{patientname}", "g")), billMaster?.appointmentAllList[0]?.patientRegistration?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'OPD_Slip' ?
                  matches?.replace((new RegExp("{patientname}", "g")), billMaster?.appointmentData?.patientRegistration?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'prescription' ?
                  matches?.replace((new RegExp("{patientname}", "g")), billMaster?.regDetails?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  : ''

              const placeholders = WithoutReplace?.split(",")?.map(s => s.trim())?.map(s => ({ text: s })) || ""; 

              bodyReplaceMessage = bodyMessage.replace(/{{(\d+)}}/g, (_, n) => bodyArray[Number(n) - 1] || "");


             this.whatsappcontent = 
             
             category === 'OP' ?
             bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.firstname)
                  .replace((new RegExp("{billnumber}", "g")),billMaster?.billData?.billMaster?.billNumber || 0)
                  .replace((new RegExp("{billdate}", "g")),this.datService.dateUtctoLocal(billMaster?.billData?.billMaster?.billDate,"DD-MMM-YYYY"))
                  .replace((new RegExp("{netamount}", "g")),billMaster?.billData?.billMaster?.netAmount || 0)
                  .replace((new RegExp("{hospitalname}", "g")),localStorage.getItem('hospitalName'))
                  .replace((new RegExp("{regno}", "g")), billMaster?.patientDetails?.regNum || 0)
                  .replace((new RegExp("{balanceamount}", "g")),billMaster?.billData?.billMaster?.balanceAmount || 0)         
                  .replace((new RegExp("{hospitalname}", "g")), localStorage.getItem('hospitalName'))      
   
                  .replace((new RegExp("{date}", "g")), this.datService.dateUtctoLocal(new Date(),"DD-MMM-YYYY")) 
                  .replace((new RegExp("{time}", "g")), this.datService.dateUtctoLocal(new Date(),"h:mm a"))                   

                  .replace((new RegExp("{hospitalbranch}", "g")), billMaster?.branchData?.branchName) 
                  .replace((new RegExp("{hospitalemailid}", "g")), billMaster?.branchData?.email) 
                  .replace((new RegExp("{hospitalemail}", "g")), billMaster?.branchData?.email) 
                  .replace((new RegExp("{currency}", "g")), billMaster?.branchData?.currencySymbol)    
                  .replace((new RegExp("{hospitalphonenumber}", "g")),localStorage.getItem('HospitalMobile'))
              :
              category === 'Lab' ? 
                  bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.regMst?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'IP' ? 
                  bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.patientDetails?.firstname || '')
                          .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'Appointment_Slip' ?
                  bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.appointmentAllList[0]?.patientRegistration?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  :
              category === 'OPD_Slip' ?
                  bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.appointmentData?.patientRegistration?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  : ''  
              category === 'prescription' ?
                  bodyReplaceMessage?.replace((new RegExp("{patientname}", "g")), billMaster?.regDetails?.firstname || '')
                              .replace((new RegExp("{pdfLink}", "g")), headerUrl || '')
                  : ''                  
              

              // const placeholders = matches.map(m => ({ TEXT: m }));

              if(istemplatepurchase?.whatsappCreditBalance > 0){

                this.whatsAppToken = istemplatepurchase?.businessAppTokenNo
                this.whatsAppUrl = istemplatepurchase?.whatsappApiUrl
                
                const whatsappSentItems = {
                  phone : category === 'OP' ? (billMaster?.patientDetails?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.patientDetails?.mobile) : category === 'Lab' ? ( billMaster?.patientDetails?.regMst?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.patientDetails?.regMst?.mobile ) : category === 'IP' ? (billMaster?.patientDetails?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.patientDetails?.mobile) : category === 'Appointment_Slip' ? (billMaster?.appointmentAllList[0]?.patientRegistration?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.appointmentAllList[0]?.patientRegistration?.mobile) : category === 'OPD_Slip' ? (billMaster?.appointmentData?.patientRegistration?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.appointmentData?.patientRegistration?.mobile) : category === 'prescription' ? (billMaster?.regDetails?.mobileCountryCode?.replace(/\+/g, "") + billMaster?.regDetails?.mobile) : '' , 
                  templateName : metaApiDetails?.name || '', 
                  parameters : placeholders,
                  languageCode : metaApiDetails?.language,
                  imgLink : headerUrl ?? null,
                  showTextOrImage : showTextOrImage ?? null ,
                  fileName : filename ?? null


                }
                
                if(whatsappSentItems.phone && whatsappSentItems.templateName && whatsappSentItems.parameters && this.whatsAppToken && this.whatsAppUrl){
                  this.whatsappService.sendMessage(whatsappSentItems , this.whatsAppToken , this.whatsAppUrl).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => 
                    {
                    if(data){
                        const whatsAppPostData = {
                          "branchId":this.branchId,
                          "tenantId":this.tenantId,
                          "dateTime": new Date(),
                          "createdby": parseInt(localStorage.getItem("userId")),
                          "mobileNumber": whatsappSentItems?.phone,
                          "referencenum": category === 'OP' ? billMaster?.patientDetails?.regNum ?? 0 : category === 'Lab' ? billMaster?.patientDetails?.regMst?.regNum ?? 0 : category === 'IP' ? billMaster?.patientDetails?.regNum ?? 0: category === 'Appointment_Slip' ? billMaster?.appointmentAllList[0]?.patientRegistration?.regNum ?? 0 : category === 'OPD_Slip' ? billMaster?.appointmentData?.patientRegistration?.regNum ?? 0 : category === 'prescription' ? billMaster?.regDetails?.regNum ?? 0 : '' ,
                          "referencefrom": pageName+'-'+option,
                          "message": this. whatsappcontent,
                          "smsUnit":1,
                          "senderId":null,
                          "status":"Sent",
                          "templateId":null,
                          "createdIp":null,
                          "createddate": new Date(),
                          "whatsappReferenceId":data?.messages[0]?.id ?? ''
                        }
                        this.rolesService.create(`${this.apiUrl}/smstrackingdet/${this.tenantId}/${this.branchId}/sendWhatsapp`,whatsAppPostData).pipe(takeUntil(this.unsubscribe$)).subscribe((data: any) => {
                          this.toaster.ForSuccess( `Whatsapp Send Sucessfully` );
                        })
                      }else{
                        this.toaster.ForDanger(` Whatsapp Send Unsucessfully`);
                      }
                      },(error)=>{
                        this.toaster.ForDanger(` Whatsapp Send Unsucessfully`);
                      })
                    }
                   

              }else{
                 this.toaster.ForDanger('Whatsapp Credit Balance is 0');
              }
          }          

        })
       
      }

      }

}
//DownloadPdfForSaveReportABHA
async downloadPDFsaveReportABHA(data2,visitId,resourceType,regId,admissionId,labWorkOrderId,comp?,event?){
    await this.loadPSF();
    let categoryType = resourceType;
    let subCategoryType: string = null;
    // ABHA "Save Report" PDFs were coming out blurry: a fixed scale:1.5
    // html2canvas capture re-encoded as lossy JPEG (quality 0.75) introduces
    // ringing/blur artifacts around text and table borders. Rendering at a
    // higher scale and keeping the capture as lossless PNG fixes this.
    // Scoped to comp?.isAbhaSave (set only by CareContextLinkingComponent)
    // so every other caller of this shared method — billing, lab,
    // prescription print, vaccine schedule, etc. — keeps its existing
    // scale/JPEG behavior completely unchanged.
    //
    // The `'NONE'` image compression level picked below (see addImage call)
    // embeds that PNG bitmap into the PDF completely uncompressed, on top of
    // the higher scale:2 capture — for a long table (e.g. DiagnosticReport's
    // per-test-result rows, which can span several pages) this blows the PDF
    // Blob up to tens of MB, and the resulting upload in callAtch() then gets
    // rejected by the API/gateway with 413 Request Entity Too Large, which
    // surfaces here as a failed "Save Report" for that HI Type. FlateEncode
    // ("SLOW", jsPDF's own maximum compression level for image streams) is
    // lossless — it shrinks the PDF without touching a single pixel, so this
    // is unrelated to the blur fix above and safe to change independently.
    const isAbhaSave = !!comp?.isAbhaSave;
    const canvasScale = isAbhaSave ? 2 : 1.5;
    const canvas = await html2canvas(data2, { scale: canvasScale });
    this.psDetails.orientation = "Portrait";
    this.psDetails.paperSize = "A4";
    let pdf = new jspdf(this.psDetails.orientation === "Portrait" ? 'p' : 'l', 'cm', (this.psDetails.paperSize).toLowerCase(), true);
    var width = pdf.internal.pageSize.getWidth();
    var height = pdf.internal.pageSize.getHeight();
    const imgFormat = isAbhaSave ? 'PNG' : 'JPEG';
    const imgData = isAbhaSave ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.75);

    const imgWidth = width; // Use the full page width
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = pdf.internal.pageSize.getHeight();
    // `addImage(imgData, format, x, y, w, h)` always draws the WHOLE source
    // image resized to exactly w×h — it never crops. The previous loop drew
    // the same full image on every page with a SHRINKING height
    // (`min(pageHeight, imgHeight - position)`) at an ever-increasing
    // *positive* y, which (a) on page 1 squeezed the entire (possibly
    // multi-page-tall) image into a single page's height whenever imgHeight
    // exceeded pageHeight — a vertical squash that reads as blur, worse the
    // more content overflows one page — and (b) on every later page drew
    // that same full image at y >= pageHeight, i.e. already below that
    // page's own visible area, producing a blank page. More pages were
    // added for every remaining `pageHeight` of overflow even though none
    // of them ever showed anything.
    //
    // The standard fix: draw the image at its one true, full, undistorted
    // size (imgWidth × imgHeight) on every page, and instead move it with a
    // *negative* y offset so each page's fixed viewport (0..pageHeight)
    // reveals a different vertical slice of that same unscaled image —
    // never resized, so never blurred, and the loop below naturally stops
    // once the whole image has been covered (no extra blank pages).
    let position = 0;
    let heightLeft = imgHeight;
    pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight, undefined, isAbhaSave ? 'SLOW' : 'FAST');
    heightLeft -= pageHeight;
    // `imgHeight` is derived from html2canvas's captured canvas.width/height,
    // which come from the DOM element's (integer-rounded) offsetWidth/
    // offsetHeight — not the exact 210mm:297mm ratio the CSS asks for. That
    // rounding can leave `heightLeft` a hair above 0 even when everything
    // actually fit on page 1 (e.g. WellnessRecord with only a couple of
    // sections), which would otherwise add one fully blank trailing page for
    // a sliver of content nobody can see. Only continue when there's more
    // than a negligible amount of real content left — comfortably above any
    // rounding noise, comfortably below a genuine short page (e.g. just the
    // printer footer spilling onto its own page, as DiagnosticReport's does).
    const MIN_MEANINGFUL_PAGE_HEIGHT_CM = 0.5;
    while (heightLeft > MIN_MEANINGFUL_PAGE_HEIGHT_CM) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight, undefined, isAbhaSave ? 'SLOW' : 'FAST');
        heightLeft -= pageHeight;
    }
    let filename="";
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}:${now.getSeconds().toString().padStart(2,'0')}`;
    if(resourceType==="Immunization"){
       filename = "Immunization_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="Prescription"){
      categoryType='Pharmacy';
      filename = "OP_Prescription_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="DischargeSummary"){
      filename = "Discharge_summary_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="Lab"){
      filename = "Diagnostic_Report_For_Work_Order_Id_"+labWorkOrderId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="IP_Prescription"){
      categoryType='Pharmacy';
      filename = "IP_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="OP_Billing"){
      categoryType='Billing';
      subCategoryType=resourceType;
      filename =  "OP_Billing_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="IP_Billing"){
      categoryType='Billing';
      subCategoryType=resourceType;
      filename =  "IP_Billing_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="Pharmacy_Billing"){
        categoryType='Billing';
        subCategoryType=resourceType;
        filename =  "Pharmacy_Billing_Report_For_Reg_Id_"+regId+"_"+currentTime+ ".pdf";
    }

    // ABHA "Save Report" category/subCategory/fileName, derived from the
    // existing resourceType via ABHA_HI_TYPE_BLOB_CONFIG above instead of
    // separate switches, so the three values can't drift out of sync.
    // Gated on comp?.isAbhaSave (set only by CareContextLinkingComponent)
    // rather than resourceType alone, because resourceType values like
    // "Immunization" and "Prescription" are also used by unrelated non-ABHA
    // callers of this same method. Also fixes the previous 500: OPConsultation/
    // DiagnosticReport/WellnessRecord have no branch above, so they
    // previously fell through with an empty filename ("") and categoryType
    // left as the literal resourceType string (e.g. "OPConsultation"),
    // neither of which the attachment API accepts.
    if (comp?.isAbhaSave) {
      const abhaConfig = PdfToBlobService.ABHA_HI_TYPE_BLOB_CONFIG[resourceType] ?? null;
      if (abhaConfig) {
        categoryType = abhaConfig.category;
        subCategoryType = abhaConfig.subCategory;
        const isAdmissionBased = resourceType === 'DischargeSummary';
        const referenceLabel = isAdmissionBased ? 'Admission' : 'Visit';
        const referenceId = isAdmissionBased ? admissionId : visitId;
        filename = `${abhaConfig.fileName}_For_${referenceLabel}_Id_${referenceId}_${currentTime}.pdf`;
      }
    }

    const pdfcontent = pdf.output('blob');
    if(resourceType!=="IP_Prescription" && resourceType!=="Prescription" && resourceType!=="DischargeSummary" && resourceType!=="OP_Billing" && resourceType!=="IP_Billing" && resourceType!=="Pharmacy_Billing" && resourceType!='Lab'){
      if(resourceType === "Immunization"){
        data2.style.visibility = "hidden";
      } else {
        data2.style.display = "none";
      }
    }
    this.callAtch(pdfcontent, filename, regId, visitId, admissionId, labWorkOrderId, categoryType, subCategoryType, comp, event);
  }

//DownloadPdfForSaveReportABHA

  async downloadPDF(data2,visitId,resourceType,regId,admissionId,labWorkOrderId,comp?,event?){
    await this.loadPSF();
    let categoryType = resourceType;
    let subCategoryType: string = null;
    const canvas = await html2canvas(data2);
    this.psDetails.orientation = "Portrait";
    this.psDetails.paperSize = "A4";
    // `true` enables jsPDF's own lossless FlateEncode compression for the
    // embedded page images (this addImage call below doesn't pass an
    // explicit compression level, so jsPDF applies its own "SLOW"/maximum
    // level once this flag is on) — same 413-avoidance fix as
    // downloadPDFsaveReportABHA, without touching capture scale/format/quality.
    let pdf = new jspdf(this.psDetails.orientation === "Portrait" ? 'p' : 'l', 'cm', (this.psDetails.paperSize).toLowerCase(), true);
    var width = pdf.internal.pageSize.getWidth();
    var height = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = width; // Use the full page width
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 0;
    const pageHeight = pdf.internal.pageSize.getHeight();
    while (position < imgHeight) {
        // Add a new page if content doesn't fit
        if (position !== 0) {
            pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, Math.min(pageHeight, imgHeight - position));
        position += pageHeight;
    }
    var pageCount = pdf.internal.pages.length;
    if (pageCount > 2) {
      var pageToDeleteIndex = pageCount - 1; // Index of the last page
      pdf.deletePage(pageToDeleteIndex);
    }
    let filename="";
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}:${now.getSeconds().toString().padStart(2,'0')}`;
    if(resourceType==="Immunization"){
       filename = "Immunization_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="Prescription"){
      categoryType='Pharmacy';
      filename = "OP_Prescription_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="DischargeSummary"){
      filename = "Discharge_summary_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }else if(resourceType==="Lab"){
      filename = "Diagnostic_Report_For_Work_Order_Id_"+labWorkOrderId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="IP_Prescription"){
      categoryType='Pharmacy';
      filename = "IP_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="OP_Billing"){
      categoryType='Billing';
      subCategoryType=resourceType;
      filename =  "OP_Billing_Report_For_Visit_Id_"+visitId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="IP_Billing"){
      categoryType='Billing';
      subCategoryType=resourceType;
      filename =  "IP_Billing_Report_For_Admission_Id_"+admissionId+"_"+currentTime+ ".pdf";
    }
    else if(resourceType==="Pharmacy_Billing"){
        categoryType='Billing';
        subCategoryType=resourceType;
        filename =  "Pharmacy_Billing_Report_For_Reg_Id_"+regId+"_"+currentTime+ ".pdf";
    }

    const pdfcontent = pdf.output('blob');
    if(resourceType!=="IP_Prescription" && resourceType!=="Prescription" && resourceType!=="DischargeSummary" && resourceType!=="OP_Billing" && resourceType!=="IP_Billing" && resourceType!=="Pharmacy_Billing" && resourceType!='Lab'){
      if(resourceType === "Immunization"){
        data2.style.visibility = "hidden";
      } else {
        data2.style.display = "none";
      }
    }
    this.callAtch(pdfcontent, filename, regId, visitId, admissionId, labWorkOrderId, categoryType, subCategoryType, comp, event);
  }
   
  callAtch(data,filename,regId,visitId,admissionId,labWorkOrderId,categoryType,subCategoryType,comp?,event?): void{
    if(comp){
      comp.loadPer = 80;
      comp.changeDetectorRef?.detectChanges();
    }
    const file = new File([data], filename, { type: 'application/pdf' });
    const formData: FormData = new FormData();
    formData.append('fileKey', file,file.name);

    let postData = {
      regId: regId,
      tenantId: this.tenantId,
      branchId: this.branchId,
      containerName: this.tenantId,
      fileLocation: `${this.tenantId}/${this.branchId}/${regId}/${this.datService.newDate(this.datePipe.transform(new Date(),'mediumDate'))}/${file.name}`,
      fileName: file.name,
      category: categoryType,//
      subCategory: subCategoryType || null,//
      doneDate: this.datService.currentDate(),
      doneBy: "abha",
      fileAddedBy: "system",
      notes: null,//
      tagType: "none",
      fileAddedFrom:"ABHAAtch",//
      isEdit:true,
      isCreate:true,
      WhatsappSentStatus:"N",
      WhatsappSentDateTime:new Date(),
      RegId: regId,
      VisitId:visitId,
      AdmissionId : admissionId,
      WorkOrderId : labWorkOrderId,
      createdBy: parseInt(localStorage.getItem("userId")),
      createdDate: new Date()
    }

    for (const prop in postData) {
      if (!postData.hasOwnProperty(prop)) { continue; }
      formData.append(prop , postData[prop]);
    }

    this.radiologySrvc.create(`${this.apiUrl}/AtchDet/${this.tenantId}/${this.branchId}/atch`,formData).pipe(takeUntil(this.unsubscribe$)).subscribe((datam: any) => {
    }, (error) => {
      this.toaster.ForDanger('PDF Generation Failed. Please try again.');
      if(comp){
        comp.onDownloadError();
      }
      if(event){
        event.target.disabled = false;
      }
    }, () => {
      if(comp){
        comp.loadPer = 100;
        comp.showPer = false;
        comp.buttonsDisabled = false;
        comp.changeDetectorRef?.detectChanges();
      }
      if(event){
        event.target.disabled = false;
      }
      this.toaster.ForSuccess('PDF Generated Sucessfully');
    });
  }
}
